import { useState, useEffect } from 'react';
import { BREAKPOINTS, MEDIA, matchesMedia } from '../utils/breakpoints';

// Classify the device with the same breakpoints the CSS uses:
//   phone (isMobile)  width <= 480
//   tablet            width <= 1024, or any touch-first (coarse pointer) device
//   desktop           everything else
export const detectDevice = ({ width, height, userAgent = '', hasTouchEnd = false, coarsePointer = false }) => {
  // iPadOS reports itself as a Mac; touch support gives it away
  const isIPad = /iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && hasTouchEnd);
  const isMobile = width <= BREAKPOINTS.phone;
  const isTablet = !isMobile && (width <= BREAKPOINTS.laptop || coarsePointer || isIPad);
  return {
    isMobile,
    isTablet,
    isIPad,
    isDesktop: !isMobile && !isTablet,
    isLandscape: width > height,
  };
};

const readDevice = () => detectDevice({
  width: window.innerWidth,
  height: window.innerHeight,
  userAgent: navigator.userAgent,
  hasTouchEnd: 'ontouchend' in document,
  coarsePointer: matchesMedia(MEDIA.coarsePointer),
});

const useDeviceDetect = () => {
  // Computed on first render so phones don't flash the desktop layout
  const [device, setDevice] = useState(readDevice);

  useEffect(() => {
    // Keep the old object when nothing changed so resizes don't re-render consumers
    const update = () => setDevice((prev) => {
      const next = readDevice();
      return Object.keys(next).every((k) => next[k] === prev[k]) ? prev : next;
    });
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return device;
};

export default useDeviceDetect;
